import { baseApi } from './baseApi';
import { mapAssetToFolderManager, simpleSlugify } from '../helpers';
import { FolderManagerMimeTypes } from '../interfaces';
import { addNotification } from '../store';
import { isAbortError } from '../helpers/apiErrors';

const mapApiToStore = (item: FolderFromApi): Partial<FolderManagerItem> => ({
  id: item?.ID,
  name: item?.Name,
  displayName: item?.DisplayName,

  owner: item?.Owner as any,
  createdAt: item.CreatedAt!,
  updatedAt: item?.UpdatedAt || null,
  deletedAt: item?.DeletedAt || null,
  trashedAt: item?.TrashedAt || null,
  allowed_mime_types: item?.AllowedMimeTypes ?? undefined,
  isShared: item?.IsShared,
  isSlideRepositoryFolder: item?.IsSlideRepositoryFolder,
  isThemeFolder: item?.IsThemeFolder,

  // augmented
  type: FolderManagerMimeTypes.FOLDER,
  slug: simpleSlugify(item?.DisplayName || item?.Name), // this is needed for use in main side nav to match active state

  // relations
  sourceId: item?.SourceID,
  source: item.Source as any,
  parentId: item.ParentFolderID,
  parent: item.ParentFolder as any,
});

const mapPathToStore = (pathItem: FolderFromApi) => ({
  id: pathItem.ID,
  name: pathItem?.DisplayName || pathItem.Name,
});

export const foldersService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllFolders: builder.query<FolderFromApi[], void>({
      query: () => `/folders`,
      transformResponse: (result: IApiV2Response<FolderFromApi>) => result.data,
      providesTags: () => [{ type: 'Folders', id: 'ALL' }],
    }),
    getUserRootFolders: builder.query<FolderManagerItem[], void>({
      query: () => `/folders/me`,
      transformResponse: (result: any) => (result.data || []).map(mapApiToStore),
      providesTags: () => [{ type: 'Folders', id: `LIST_USER` }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch user folders!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getSubFolders: builder.query<FolderManagerItem[], { parentId: number }>({
      query: ({ parentId }) => `/folders/${parentId}/folders`,
      transformResponse: (result: any) => (result.data || []).map(mapApiToStore),
      providesTags: (result, error, { parentId }) => [{ type: 'Folders', id: `LIST_${parentId}` }, { type: 'Folders' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch sub folders!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getFolderGrants: builder.query<IGrant[], { folderId: number }>({
      query: ({ folderId }) => `/folders/${folderId}/grants`,
      transformResponse: (result: IApiResponse<IGrant[]>) => result.data,
      providesTags: (result, error, { folderId }) => [
        {
          type: 'Folders',
          id: `GRANTS_${folderId}`,
        },
        { type: 'Folders' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch folder grants!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getUserFolderGrants: builder.query<{ role: string }, { folderId: number }>({
      query: ({ folderId }) => `/folders/${folderId}/me`,
      transformResponse: (result: IApiResponse<{ role: string }>) => result.data,
      providesTags: (result, error, { folderId }) => [
        {
          type: 'Folders',
          id: `USER_GRANTS_${folderId}`,
        },
        { type: 'Folders' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch user folders grants!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getFolderAssets: builder.query<FolderManagerItem[], { parentId: number }>({
      query: ({ parentId }) => `/folders/${parentId}/assets`,
      transformResponse: (result: IApiResponse<AssetFromApi[]>) => {
        if (!result.data) return [];
        return result.data.map(mapAssetToFolderManager) as FolderManagerItem[];
      },
      providesTags: (result, error, { parentId }) => [{ type: 'Folders', id: `ASSETS_${parentId}` }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch folder assets!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getFolderPath: builder.query<FolderManagerItem[], { folderId: number }>({
      query: ({ folderId }) => `/folders/${folderId}/path`,
      transformResponse: (result: any) => (result.data || []).map(mapPathToStore),
      providesTags: (result, error, { folderId }) => [{ type: 'Folders', id: `PATH_${folderId}` }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch folder path!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getRecentFolders: builder.query<FolderManagerItem[], void>({
      query: () => `/folders/me/recent`,
      transformResponse: (result: any) => (result.data || []).map(mapApiToStore),
      providesTags: (result, error) => [{ type: 'Folders', id: `RECENT` }, { type: 'Folders' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = error?.error?.message || error?.message || 'Failed to fetch recent folders details!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getFolderById: builder.query<FolderManagerItem, { folderId: number }>({
      query: ({ folderId }) => `/folders/${folderId}`,
      transformResponse: (result: any) => mapApiToStore(result.data) as FolderManagerItem,
      providesTags: (result, error, { folderId }) => [{ type: 'Folders', id: `GET_${folderId}` }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch folder details!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getMimeTypes: builder.query<IMimeType[], void>({
      query: () => `/mime-types`,
      transformResponse: (result: any) => result?.data,
      providesTags: () => [{ type: 'Folders', id: `GET_MIME_TYPES` }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch mime types!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getSharedWithMeFolders: builder.query<FolderManagerItem[], void>({
      query: () => `/folders/me/shared`,
      transformResponse: (result: any) => (result?.data || []).map(mapApiToStore),
      providesTags: () => [{ type: 'Folders', id: `GET_SHARED_FOLDERS` }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch shared folders!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getTrashFolders: builder.query<FolderManagerItem[], void>({
      query: () => `/folders/me/trash`,
      transformResponse: (result: any) => (result?.data || []).map(mapApiToStore),
      providesTags: () => [{ type: 'Folders', id: `GET_REMOVED_FOLDERS` }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch trash folders!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getTrashAssets: builder.query<FolderManagerItem[], void>({
      query: () => `/assets/me/trash`,
      transformResponse: (result: any) => (result?.data || []).map(mapAssetToFolderManager),
      providesTags: () => [{ type: 'Folders', id: `GET_REMOVED_ASSETS` }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch trash assets!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    addSubFolder: builder.mutation<void, { parentId: number; data: IAddFolderPayload }>({
      query: ({ parentId, data }) => ({
        url: `/folders`,
        method: 'POST',
        body: {
          name: data.name,
          allowed_mime_types: data.allowed_mime_types,
          is_slide_repository_folder: data.is_slide_repository_folder,
          parentFolderId: parentId,
        },
      }),
      invalidatesTags: (result, error, { parentId }) => [{ type: 'Folders', id: `LIST_${parentId}` }],
    }),
    updateFolder: builder.mutation<void, IUpdateFolderProps>({
      query: ({ folderId, data }) => ({
        url: `/folders/${folderId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: () => [{ type: 'Folders' }],
    }),
    shareFolder: builder.mutation<void, { folderId: number; data: SharePayload }>({
      query: ({ folderId, data }) => ({
        url: `/folders/${folderId}/grants`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: () => [{ type: 'Folders' }],
    }),
    revokeFolder: builder.mutation<void, { folderId: number; groupId: number }>({
      query: ({ folderId, groupId }) => ({
        url: `/folders/${folderId}/grants`,
        method: 'DELETE',
        body: {
          group_id: groupId,
        },
      }),
      invalidatesTags: () => [{ type: 'Folders' }],
    }),
    copyFolders: builder.mutation({
      query: ({ targetId, query, data }) => ({
        url: `/folders/${targetId}/copy${query ? query : ''}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: () => [{ type: 'Folders', id: 'LIST' }],
    }),
    deleteFolderById: builder.mutation({
      query: ({ folderId }) => ({
        url: `/folders/${folderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: 'Folders' }],
    }),
  }),
});

export const {
  useGetAllFoldersQuery,
  useGetFolderByIdQuery,
  useGetMimeTypesQuery,
  useGetFolderPathQuery,
  useGetSubFoldersQuery,
  useGetFolderAssetsQuery,
  useGetFolderGrantsQuery,
  useGetRecentFoldersQuery,
  useGetUserFolderGrantsQuery,
  useGetTrashAssetsQuery,
  useGetTrashFoldersQuery,
  useGetUserRootFoldersQuery,
  useGetSharedWithMeFoldersQuery,

  useAddSubFolderMutation,
  useUpdateFolderMutation,
  useCopyFoldersMutation,
  useShareFolderMutation,
  useRevokeFolderMutation,
  useDeleteFolderByIdMutation,
} = foldersService;
